import { CellAlign, CellType, HeadCell } from '@dfl/mui-admin-layout';
import { ReactLink } from '@dfl/react-security';
import { DateValue, FlexBox } from '@dfl/mui-react-common';
import { Chip, Typography } from '@mui/material';
import { addressColumn, createdATColumn, } from 'modules/common/constants';
import { providerEmailColumn, providerPhoneColumn } from 'modules/inventory/provider/common/constants';
import { providerCommissionColumn } from 'modules/inventory/provider/supplier/constants';
import { AvatarNameCell } from 'modules/common/components/AvatarNameCell';
import { IConciliation } from '../interfaces';
import { subOrderCodeColumn, subOrderDistributionCenterColumn } from 'modules/sales/sub-orders/constants/sub-order.columns';
import { orderDeliveryDueDateColumn, orderTotalProductColumns } from 'modules/sales/common/constants/order-columns';
import { IOrder } from 'modules/sales/common/interfaces/IOrder';
import { IAddress } from 'lib/dfl-address-form-mui/interfaces';
import PriceCell from 'modules/security/audit-logs/components/TableCells/PriceCell';
import { DASHBOARD_COLOR } from 'modules/dashboard/interfaces/IDashboardAnalytic';

const logisticNameColumn = {
  field: 'name',
  headerName: 'common:name',
  disablePadding: true,
  renderCell: (name: string, data: any) => (
    <AvatarNameCell
      link={`/conciliations/main/logistics/${data._id as string}/logistics`}
      image={data?.avatar}
      name={name}
    />
  ),
};

export const conciliationOrdersColumn: HeadCell = {
  field: 'metrics.orders',
  headerName: 'conciliation:orders',
  align: CellAlign.CENTER,
  type: CellType.NUMBER,
};

export const conciliationCodeColumn = {
  field: 'code',
  headerName: 'common:code',
  renderCell: (name: string, data: any) => (
    <AvatarNameCell
      hideImage
      link={`/conciliations/${data._id as string}/products`}
      name={name}
    />
  ),
};

export const totalAmountSoldColumn = {
  field: 'metrics.totalAmountSold',
  align: CellAlign.CENTER,
  cellClassName: 'font-bold',
  headerName: 'common:total',
  type: CellType.NUMBER,
  renderCell: (value: number) => <PriceCell value={value || 0} />
}

export const refundToStockColumn = {
  field: 'metrics.refundToStock',
  align: CellAlign.CENTER,
  headerName: 'conciliation:fields.refundToStock',
  type: CellType.NUMBER,
  renderCell: (value: number) => <PriceCell value={value || 0} />
}

export const conciliationPeriodColumn = {
  field: 'period',
  headerName: 'common:period',
  renderCell: (value: string, data: IConciliation) => (
    <ReactLink to={`/conciliations/${data._id}/products`} underline={'hover'}>
      <DateValue value={data?.startDate} />
      <Typography mx={0.5} component={'span'}>
        ~
      </Typography>
      <DateValue value={data?.endDate} />
    </ReactLink>
  ),
};

export const totalChargesColumn = {
  field: 'metrics.totalCharges',
  align: CellAlign.CENTER,
  headerName: 'stock:totalCharges',
  renderCell: (value: number) => <PriceCell value={value || 0} />
}

export const totalHandlingAmountColumn = {
  field: 'metrics.totalHandlingAmount',
  align: CellAlign.CENTER,
  headerName: 'conciliation:totalHandlingAmount',
  renderCell: (value: number) => <PriceCell value={value || 0} />
}

export const totalStockChargesColumn = {
  field: 'metrics.totalStockCharge',
  align: CellAlign.CENTER,
  headerName: 'conciliation:stockCharges',
  renderCell: (value: number) => <PriceCell value={value || 0} />
}

export const totalLogisticCommissionAmountColumn = {
  field: 'metrics.totalLogisticCommissionAmount',
  align: CellAlign.CENTER,
  headerName: 'conciliation:totalLogisticCommissionAmount',
  renderCell: (value: number) => <PriceCell value={value || 0} />
}

export const deliveryOtherCostAmountColumn = {
  field: 'metrics.deliveryOtherCostAmount',
  align: CellAlign.CENTER,
  headerName: 'conciliation:deliveryOtherCostAmount',
  renderCell: (value: number) => <PriceCell value={value || 0} />
}

export const totalUnitsSoldColumn = {
  field: 'metrics.totalUnitsSold',
  headerName: 'productDiscount:analytic.quantity',
  align: CellAlign.CENTER,
  type: CellType.NUMBER,
}

export const refundsAmountColumn = {
  field: 'metrics.refundsAmount',
  align: CellAlign.CENTER,
  translate: true,
  headerName: 'incidenceResolution:refunds',
  renderCell: (value: number) => <PriceCell value={value || 0} />
}

export const productOtherCostAmountColumn = {
  field: 'metrics.productOtherCostAmount',
  align: CellAlign.CENTER,
  translate: true,
  headerName: 'conciliation:fields.productOtherCostAmount',
  renderCell: (value: number) => <PriceCell value={value || 0} />
}

export const productOtherCostGrossAmountColumn = {
  field: 'metrics.productOtherCostGrossAmount',
  align: CellAlign.CENTER,
  translate: true,
  headerName: 'conciliation:fields.productOtherCostGrossAmount',
  renderCell: (value: number) => <PriceCell value={value || 0} />
}

export const productOtherCostRefundAmountColumn = {
  field: 'metrics.productOtherCostRefundAmount',
  align: CellAlign.CENTER,
  translate: true,
  headerName: 'conciliation:fields.productOtherCostRefundAmount',
  renderCell: (value: number) => <PriceCell value={value || 0} />
}

export const conciliationSupplierColumn: HeadCell = {
  field: 'supplier',
  headerName: 'common:provider.SUPPLIER',
  disablePadding: false,
  sortable: false,
  renderCell: (_, data: any) => (
    <FlexBox gap={1}>
      <AvatarNameCell
        name={data?.supplier?.name}
        hideImage
        link={`/inventory/provider/suppliers/${data?.supplier?._id as string}/general`}
      />
      {data?.isOwnProduct && <Chip color="primary" size="small" sx={{ width: 10, height: 10 }} />}
    </FlexBox>
  ),
};

export const totalAdjustmentAmountColumn = {
  field: 'metrics.totalAdjustmentAmount',
  align: CellAlign.CENTER,
  translate: true,
  headerName: 'stock:totalAdjustmentAmount',
  renderCell: (value: number) => <PriceCell value={value || 0} />
}

export const amountToPayColumn = {
  field: 'metrics.amountToPay',
  headerName: 'stock:amountToPay',
  align: CellAlign.CENTER,
  headerAlign: CellAlign.CENTER,
  cellClassName: 'font-bold min-w-[150px]',
  renderCell: (value: number, data: any) => {
    const providerPayBack = data?.metrics?.providerPayBack;
    const displayValue = (providerPayBack !== 0 && providerPayBack != null && value === 0) ? -(providerPayBack) : value;
    return (
      <PriceCell
        value={displayValue || 0}
        sx={{ color: (providerPayBack !== 0 && providerPayBack != null && value === 0) ? DASHBOARD_COLOR.RED_DARK : undefined }}
      />
    )
  },
}

export const orderLocationColumn: HeadCell<IOrder> = {
  field: 'shipping.address',
  headerName: 'order:shipping.address.location',
  width: 200,
  renderCell: (value: IAddress) => {
    const location = [value?.cityName, value?.stateName, value?.countryName].filter(Boolean).join(', ');
    return <Typography>{location}</Typography>
  },
};

export const orderAmountToPayColumn = {
  field: 'costDetails.values.distributionCenter',
  headerName: 'stock:amountToPay',
  cellClassName: 'font-bold',
  align: CellAlign.CENTER,
  renderCell: (value: number) => {
    return <PriceCell value={value || 0} />
  },
}

export const conciliationOrderColumns: Array<HeadCell<any>> = [
  subOrderCodeColumn(),
  orderLocationColumn,
  subOrderDistributionCenterColumn,
  orderTotalProductColumns,
  orderDeliveryDueDateColumn,
  orderAmountToPayColumn
];

export const mainConciliationColumns: HeadCell[] = [
  conciliationCodeColumn,
  conciliationPeriodColumn,
  {
    field: 'metrics.amountToPaySupplier',
    headerName: 'conciliation:product',
    align: CellAlign.CENTER,
    renderCell: (value: number) => <PriceCell value={value} sx={{ color: value < 0 ? 'red' : 'black' }} />,
  },
  {
    field: 'metrics.amountToPayWarehouse',
    headerName: 'breadcrumb:warehouse',
    align: CellAlign.CENTER,
    renderCell: (value: number) => <PriceCell value={value || 0} sx={{ color: value < 0 ? 'red' : 'black' }} />,
  },
  {
    field: 'metrics.amountToPayCD',
    headerName: 'distributionCenters:name',
    align: CellAlign.CENTER,
    renderCell: (value: number) => <PriceCell value={value || 0} sx={{ color: value < 0 ? 'red' : 'black' }} />,
  },
  {
    field: 'metrics.amountToPayCarrier',
    headerName: 'conciliation:carrier',
    align: CellAlign.CENTER,
    renderCell: (value: number) => <PriceCell value={value || 0} sx={{ color: value < 0 ? 'red' : 'black' }} />,
  },
  {
    field: 'metrics.totalRefundsAmount',
    headerName: 'incidenceResolution:refunds',
    align: CellAlign.CENTER,
    renderCell: (value: number) => <PriceCell value={Math.abs(value) || 0} sx={{ color: value < 0 ? 'red' : 'black' }} />,
  },
  {
    field: 'metrics.netAmountTotal',
    headerName: 'conciliation:total',
    cellClassName: 'font-bold',
    align: CellAlign.CENTER,
    renderCell: (total: number) => <PriceCell value={total} sx={{ color: total < 0 ? 'red' : 'black' }} />,
  },
];

export const conciliationLogisticColumns: Array<HeadCell<any>> = [
  logisticNameColumn,
  providerEmailColumn,
  providerPhoneColumn,
  providerCommissionColumn,
  addressColumn,
  createdATColumn,
];
